import React, { useRef, useState, useEffect } from 'react';
import InputBox from "../common/InputBox";
import SelectBox from "../common/SelectBox";
import InputArea from "../common/InputArea";
import CustomPopup from "../common/CustomPopup";

const ContactSection: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const sectionRef = useRef(null);
  const [agree, setAgree] = useState(false);
  const [agreeError, setAgreeError] = useState('');
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [form, setForm] = useState({
    company: '', person: '', email: '', phone: '', contents: ''
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({
    company: '',
    person: '',
    email: '',
    phone: '',
    contents: '',
  });

  const inputRefs: { [key: string]: React.RefObject<HTMLDivElement> } = {
    company: useRef(null),
    person: useRef(null),
    email: useRef(null),
    phone: useRef(null),
    contents: useRef(null),
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    let isValid = true;
    const newErrors: { [key: string]: string } = {
      company: '',
      person: '',
      email: '',
      phone: '',
      contents: '',
    };

    if (!form.company.trim()) {
      newErrors.company = '업체명을 입력해주세요';
      isValid = false;
    }
    if (!form.person.trim()) {
      newErrors.person = '담당자명을 입력해주세요';
      isValid = false;
    }
    if (!form.email.trim()) {
      newErrors.email = '이메일을 입력해주세요';
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = '올바른 이메일 형식이 아닙니다';
      isValid = false;
    }
    if (!form.phone.trim()) {
      newErrors.phone = '연락처를 입력해주세요';
      isValid = false;
    }
    if (!form.contents.trim()) {
      newErrors.contents = '문의 내용을 입력해주세요';
      isValid = false;
    }

    setErrors(newErrors);

    if (!isValid) {
      const firstErrorField = Object.keys(newErrors).find(key => newErrors[key]);
      if (firstErrorField && inputRefs[firstErrorField]?.current) {
        setTimeout(() => {
          const current = inputRefs[firstErrorField]?.current;
          if (current) {
            current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            const inputElement = current.querySelector('input, textarea, select') as HTMLInputElement;
            if (inputElement) {
              inputElement.focus();
            }
          }
        }, 100);
      }
    }

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      const payload = {
        company: form.company,
        person: form.person,
        email: form.email,
        phone: form.phone,
        contents: form.contents
      };

      const response = await fetch("https://hook.eu2.make.com/q7671p9dgwgkahn2jhuzl3qykjvueuz7", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        alert("문의가 성공적으로 전송되었습니다!");
        setForm({
          company: '',
          person: '',
          email: '',
          phone: '',
          contents: ''
        });
        setAgree(false);
      } else {
        throw new Error('문의 전송에 실패했습니다. 다시 시도해주세요.');
      }
    } catch (error) {
      alert('문의 전송 중 오류가 발생했습니다. 다시 시도해주세요.');
      console.error('Error submitting form:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    // 1. 입력값 벨리데이션 먼저 체크
    const isValid = validateForm();
    if (!isValid) {
      return;
    }
    // 2. 약관동의 체크는 마지막에
    if (!agree) {
      setAlertMessage('개인정보 수집 및 이용에 동의해 주세요.');
      setAlertOpen(true);
      return;
    }
    handleSubmit(e as unknown as React.FormEvent<HTMLFormElement>);
  };

  return (
    <section id="contact"
      ref={sectionRef}
      className="w-full bg-white py-16 flex flex-col items-center">
      <div className="container px-4 md:px-8 lg:px-4">
        <p className="text-center mt-[40px] mb-[60px] md:mb-[20px] lg:mb-[20px] md:mt-[60px] lg:mt-[80px] text-[28px] lg:text-[48px] font-bold mb-6">Contact</p>

        <form
          className="w-full flex flex-col gap-4 md:mt-[60px] lg:mt-[100px]"
          onSubmit={handleSubmit}>
          <div ref={inputRefs.company}>
            <InputBox
              label="업체명"
              name="company"
              required
              placeholder="업체명을 입력해주세요"
              value={form.company}
              onChange={handleChange}
              error={errors.company}
            />
          </div>
          <div ref={inputRefs.person}>
            <InputBox
              label="담당자명"
              name="person"
              required
              placeholder="담당자명을 입력해주세요"
              value={form.person}
              onChange={handleChange}
              error={errors.person}
            />
          </div>
          <div ref={inputRefs.email}>
            <InputBox
              label="이메일"
              name="email"
              type="email"
              required
              placeholder="이메일을 입력해주세요"
              value={form.email}
              onChange={handleChange}
              error={errors.email}
            />
          </div>
          <div ref={inputRefs.phone}>
            <InputBox
              label="연락처"
              name="phone"
              required
              placeholder="연락처를 입력해주세요"
              value={form.phone}
              onChange={handleChange}
              error={errors.phone}
            />
          </div>
          <div ref={inputRefs.contents}>
            <InputArea
              label="문의 내용"
              name="contents"
              required
              placeholder="문의할 내용을 입력해주세요"
              value={form.contents}
              onChange={handleChange}
              error={errors.contents}
            />
          </div>
          <div className="flex flex-col gap-2 mt-0">
            <label className="font-semibold text-2xl md:text-3xl lg:text-xl mb-1 text-left w-full">
              개인정보 수집 및 이용 동의 <span className="text-red-500">*</span>
            </label>
            <div
              className="border rounded bg-[#fafafa] p-4 text-lg md:text-2xl lg:text-lg leading-[1.6] resize-none"
              style={{ height: 200, overflowY: 'auto', whiteSpace: 'pre-line' }}>
              {`주식회사 챙기고는 ㈜챙기고에 대한 제안 내용 확인 및 검토를 위해 최소한의 개인정보를 수집하며, 수집된 정보는 아래의 목적에 따라 활용됩니다.\n\n1. 수집하는 개인정보의 항목\n- 필수 항목: 업체명, 담당자명, 이메일, 연락처, 문의 유형, 제휴 제안 내용\n- 선택 항목: 상담에 필요한 기타 정보\n\n2. 개인정보의 수집 및 이용 목적\n- 브랜드 제휴 및 입점 문의에 대한 검토 및 상담 진행\n- 서비스 관련 안내 및 공지 전달 등\n\n3. 개인정보 보유 및 이용 기간\n- 수집된 개인정보는 수집 목적이 달성되거나 제공받은 목적이 완료된 후, 당사의 내부 방침에 따라 안전하게 보관 및 파기됩니다.\n\n※ 개인정보 제공에 동의하지 않으실 경우, 상담 및 서비스 제공이 제한될 수 있습니다.`}
            </div>
            <label className="flex items-center py-5 px-5 md:px-10 mt-2 text-lg md:text-xl lg:text-lg text-left w-full justify-start md:justify-center">
              <input
                type="checkbox"
                checked={agree}
                onChange={e => {
                  setAgree(e.target.checked);
                  if (e.target.checked) setAgreeError('');
                }}
                className="mr-4 w-5 h-5 md:w-4 md:h-4"
              />
              <span className="md:hidden text-left">위 내용을 모두 확인하였으며,<br/>개인정보 수집 및 이용에 동의합니다.</span>
              <span className="hidden md:inline text-center">위 내용을 모두 확인하였으며, 개인정보 수집 및 이용에 동의합니다.</span>
            </label>
            {agreeError && <span className="text-red-500 text-[13px]">{agreeError}</span>}
          </div>
          <button
            type="button"
            onClick={handleButtonClick}
            disabled={isLoading}
            className={`mt-8 md:mt-2 bg-black text-white text-xl md:text-3xl lg:text-xl font-semibold px-14 py-4 rounded-full transition self-center shadow-lg mb-[60px] md:mb-[100px] relative ${isLoading ? 'opacity-70 cursor-not-allowed' : 'hover:bg-gray-800'}`}
            style={{ boxShadow: '0 4px 24px 0 rgba(0,0,0,0.12), inset 0 2px 8px 0 #fff8' }}
          >
            {isLoading ? (
              <>
                <span className="opacity-0">문의하기 →</span>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                </div>
              </>
            ) : (
              '문의하기 →'
            )}
          </button>
        </form>
        <CustomPopup
          open={alertOpen}
          title="알림"
          message={alertMessage}
          onClose={() => setAlertOpen(false)}
        />
      </div>
    </section>
  );
};

export default ContactSection;
